import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SquaresFour, WarningCircle, Fire, Bell, ArrowRight } from "@phosphor-icons/react";
import HelpTip from "@/components/onboarding/HelpTip";

const Stat = ({ label, value, icon: Icon, accent, help }) => (
  <div className="border p-5 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider flex items-center" style={{ color: "var(--text-2)" }}>{label}{help && <HelpTip text={help} />}</span>
      <Icon size={18} weight="duotone" color={accent} />
    </div>
    <div className="mono text-3xl font-bold mt-3" style={{ color: accent }}>{value}</div>
  </div>
);

function scoreColor(score) {
  if (score === null || score === undefined) return "var(--text-2)";
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "var(--warning)";
  return "var(--danger)";
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectWorkspace } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.portfolio().then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openWorkspace = (id) => {
    selectWorkspace(id);
    nav("/");
  };

  const rows = data?.workspaces || [];
  const totals = data?.totals;

  return (
    <div className="fade-up space-y-6 max-w-6xl">
      <div>
        <h2 className="text-base font-bold mb-1">Portfolio</h2>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Every client site at a glance — click a row to switch into that project.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Workspaces" value={totals ? totals.workspaces : "—"} icon={SquaresFour} accent="var(--accent)" help="How many separate projects (client sites) you're managing in JumpSEO." />
        <Stat label="Avg SEO Score" value={totals?.avg_score ?? "—"} icon={SquaresFour} accent={scoreColor(totals?.avg_score)} help="Average technical SEO score across all your projects' most recent audits." />
        <Stat label="Hot Leads" value={totals ? totals.leads_hot : "—"} icon={Fire} accent="var(--warning)" help="Leads scored 75+ by the AI, across every project -- your best opportunities right now." />
        <Stat label="Needs Follow-up" value={totals ? totals.needs_followup : "—"} icon={Bell} accent="var(--primary)" help="Leads that haven't been moved forward or contacted in a while and may be going cold." />
      </div>

      {totals?.needs_attention?.length > 0 && (
        <div className="border p-4 flex items-center gap-3" style={{ borderColor: "var(--danger)", background: "rgba(239,68,68,0.06)" }}>
          <WarningCircle size={20} color="var(--danger)" />
          <span className="text-sm">{totals.needs_attention.length} workspace{totals.needs_attention.length > 1 ? "s" : ""} scoring below 60 — worth a look.</span>
        </div>
      )}

      <div className="border bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold">Clients</h2>
        </div>
        {loading ? (
          <p className="text-sm p-6" style={{ color: "var(--text-2)" }}>Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm p-6" style={{ color: "var(--text-2)" }}>No workspaces yet. Create one from the project switcher.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>
                  <th className="px-6 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium">Critical</th>
                  <th className="px-4 py-3 font-medium">Leads</th>
                  <th className="px-4 py-3 font-medium">Hot</th>
                  <th className="px-4 py-3 font-medium">Content</th>
                  <th className="px-4 py-3 font-medium">Outreach Sent</th>
                  <th className="px-4 py-3 font-medium">Follow-up</th>
                  <th className="px-4 py-3 font-medium">Last Audit</th>
                  <th className="px-6 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id} onClick={() => openWorkspace(w.id)}
                    className="cursor-pointer border-t hover:bg-[#0e1626] transition-colors" style={{ borderColor: "var(--line)" }}>
                    <td className="px-6 py-3">
                      <div className="font-semibold">{w.name}</div>
                      <div className="mono text-xs truncate" style={{ color: "var(--text-2)" }}>{w.domain || "—"}</div>
                    </td>
                    <td className="px-4 py-3 mono font-bold" style={{ color: scoreColor(w.avg_score) }}>{w.avg_score ?? "—"}</td>
                    <td className="px-4 py-3 mono" style={{ color: w.critical_issues ? "var(--danger)" : "var(--text-2)" }}>{w.critical_issues ?? "—"}</td>
                    <td className="px-4 py-3 mono">{w.leads_total}</td>
                    <td className="px-4 py-3 mono" style={{ color: w.leads_hot ? "var(--warning)" : "var(--text-2)" }}>{w.leads_hot}</td>
                    <td className="px-4 py-3 mono">{w.content_count}</td>
                    <td className="px-4 py-3 mono">{w.outreach_sent}</td>
                    <td className="px-4 py-3 mono" style={{ color: w.needs_followup ? "var(--primary)" : "var(--text-2)" }}>{w.needs_followup || "—"}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-2)" }}>{timeAgo(w.last_audit_at)}</td>
                    <td className="px-6 py-3 text-right"><ArrowRight size={15} style={{ color: "var(--text-2)" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
